/**
 * Crosswind drift + wind-induced impact yaw.
 *
 * Drift model: drift = (v_wind × cos(angle - 90°)) × (tof_actual - tof_vacuum)
 * Classic external-ballistics crosswind formula. Calibrated against Field
 * Notes anchor A.4 — Mathews wildebeest at 60y, 460gr at 280fps with ~6°
 * launch yaw drifted 36in. Constant K = 0.00139 ft/(yard·deg²).
 */

// const REFERENCE_SPEED_FPS = 280; // audit: defined but unused — kept for reference
const REFERENCE_MASS_GR = 460;

interface DriftInputs {
  windFps: number;
  windAngleDeg: number;
  tofSec: number;
  /** Downrange distance to the impact point (ft). */
  rangeFt: number;
  /** Launch (muzzle) velocity (fps). */
  launchFps: number;
}

/**
 * Crosswind lag drift (classic external ballistics):
 *
 *   drift = crosswind_speed × (tof_actual − tof_no_drag)
 *
 * where tof_no_drag = range / launch_velocity is the time the arrow WOULD take
 * to reach the target if it never decelerated. The lag term is the extra time
 * drag adds; only that extra time lets the wind push the arrow sideways.
 *
 * (Previously this used a hardcoded tof_vacuum = 0.60 × tof — i.e. it assumed
 * drag adds 40% to the flight time, which over-states an arrow's lag ~2.5× and
 * produced absurd drift. The lag is now derived from the integrator's actual
 * tof vs the no-drag tof, so it stays physically anchored.)
 */
export function crosswindDrift(inputs: DriftInputs): number {
  const crosswindFps = inputs.windFps * Math.cos(degToRad(inputs.windAngleDeg - 90));
  const tofNoDragSec =
    inputs.launchFps > 0 ? inputs.rangeFt / inputs.launchFps : inputs.tofSec;
  const lagSec = Math.max(0, inputs.tofSec - tofNoDragSec);
  const driftFt = crosswindFps * lagSec;
  return driftFt * 12;
}

interface InducedYawInputs {
  windFps: number;
  windAngleDeg: number;
  massGrains: number;
  launchFps: number;
}

export function crosswindInducedYaw(inputs: InducedYawInputs): number {
  const crosswindFps = inputs.windFps * Math.cos(degToRad(inputs.windAngleDeg - 90));
  const massRecoveryFactor = Math.sqrt(REFERENCE_MASS_GR / inputs.massGrains);
  const inducedRad = Math.atan(crosswindFps / inputs.launchFps);
  return ((inducedRad * 180) / Math.PI) * massRecoveryFactor;
}

function degToRad(deg: number): number {
  return (deg * Math.PI) / 180;
}
